/**
 * FACE FORWARD — a character authored facing −Z is turned to face +Z, in the
 * bytes, before anything reads it (Allen, 2026-09-25: Tripo rendered the bear
 * "facing backward … every animation appeared to be correct — but it was all
 * facing backward"). Loaded as-is it rigs and plays; in World it would walk
 * away from its own heading. glTF's front is +Z; every other body we carry
 * faces it.
 *
 * MEASURED, never assumed: the facing is the mean foot → toe direction of the
 * bind skeleton (the portrait stage's cue; a toe is the foot's child joint). The
 * turn is taken only when that direction clearly points −Z; anything else passes
 * through untouched, with the reading.
 *
 * ANY YAW, not only a half turn (Allen, the bear girl, same day: "Tripo
 * exported the base pose … rotated around the vertical axis" — measured 33.7°
 * at the feet, 33.2° at the shoulders). The yaw is read from the feet (mean foot
 * → toe, so natural toe-out cancels) and confirmed by the shoulder line; a body
 * 10° or more off +Z is turned by exactly the measured yaw.
 *
 * THE TURN IS EXACT (R = a rotation about +Y). glTF skins a vertex as Σ J·IBM·v; three
 * (what Harry renders and exports) also applies the skinned mesh's own node
 * matrix M. So: every TOP joint and every top unskinned mesh node gets R·local;
 * a skinned primitive's positions, normals, tangents and morph deltas get
 * M⁻¹·R·M; every inverse bind matrix gets IBM·R⁻¹. The skinning is identical,
 * only turned. POSITION bounds are recomputed. The turn lives on the top joint
 * (Root), which no family clip keys — measured on the library: clips key Hip
 * and below.
 *
 * Refuses by name what it cannot turn exactly (quantized or sparse attributes),
 * rather than turning half a character.
 *
 * MEASURED ONCE, DECLARED, REPLAYED (the ADR-030 §14 law, as CENTRE THE TAIL):
 * the load door measures a NEW body and writes `target.facing` into its fresh
 * Document; `runDocument` only replays a declared turn. A Document without it —
 * every session saved before this — loads exactly as authored: a turn re-measured
 * on reopen would renumber what the Document names by geometry (a hand's
 * `branch-3`, measured on the kobold fixture that stands 90° round).
 *
 * Kernel-pure. Depends on: nothing but math. Depended on by: the app's load
 * door (measure) and run-document (replay, before both parses).
 */
import { KernelError } from '../ir/errors.js';

export class FaceForwardRefusal extends KernelError {}

export interface FaceForwardResult {
  /** The bytes to load: the input itself when nothing was turned. */
  readonly glb: ArrayBuffer;
  readonly turned: boolean;
  /** The turn applied, degrees about +Y (0 when none) — what the load door DECLARES in the Document. */
  readonly turnDeg: number;
  /** The measured foot → toe direction (mean of both feet, XZ, unnormalised), or null when no feet were found. */
  readonly facing: { readonly x: number; readonly z: number } | null;
  /** One line: what was measured and what was done. */
  readonly reason: string;
}

const GLB_MAGIC = 0x46546c67;
const CHUNK_JSON = 0x4e4f534a;
const CHUNK_BIN = 0x004e4942;
const FLOAT = 5126;

type M4 = number[];
interface Node {
  name?: string;
  children?: number[];
  translation?: number[];
  rotation?: number[];
  scale?: number[];
  matrix?: number[];
  mesh?: number;
  skin?: number;
}
interface Accessor {
  bufferView?: number;
  byteOffset?: number;
  componentType: number;
  count: number;
  type: string;
  normalized?: boolean;
  sparse?: unknown;
  min?: number[];
  max?: number[];
}
interface BufferView {
  byteOffset?: number;
  byteStride?: number;
}
interface Primitive {
  attributes: Record<string, number>;
  targets?: Record<string, number>[];
}
interface Gltf {
  nodes?: Node[];
  scenes?: { nodes?: number[] }[];
  scene?: number;
  skins?: { joints: number[]; inverseBindMatrices?: number }[];
  meshes?: { primitives: Primitive[] }[];
  accessors?: Accessor[];
  bufferViews?: BufferView[];
}

function parse(glb: ArrayBuffer): { json: Gltf; bin: Uint8Array } {
  const view = new DataView(glb);
  if (glb.byteLength < 20 || view.getUint32(0, true) !== GLB_MAGIC) throw new FaceForwardRefusal('not a GLB (bad magic)');
  let at = 12;
  let json: Gltf | null = null;
  let bin = new Uint8Array(0);
  while (at + 8 <= glb.byteLength) {
    const len = view.getUint32(at, true);
    const type = view.getUint32(at + 4, true);
    const data = new Uint8Array(glb, at + 8, len);
    if (type === CHUNK_JSON) json = JSON.parse(new TextDecoder().decode(data)) as Gltf;
    else if (type === CHUNK_BIN) bin = new Uint8Array(data);
    at += 8 + len + ((4 - (len % 4)) % 4);
  }
  if (json === null) throw new FaceForwardRefusal('the GLB has no JSON chunk');
  return { json, bin };
}

function build(json: Gltf, bin: Uint8Array): ArrayBuffer {
  const js = new TextEncoder().encode(JSON.stringify(json));
  const jp = (4 - (js.length % 4)) % 4;
  const bp = (4 - (bin.length % 4)) % 4;
  const total = 20 + js.length + jp + (bin.length > 0 ? 8 + bin.length + bp : 0);
  const out = new ArrayBuffer(total);
  const dv = new DataView(out);
  const u8 = new Uint8Array(out);
  dv.setUint32(0, GLB_MAGIC, true);
  dv.setUint32(4, 2, true);
  dv.setUint32(8, total, true);
  dv.setUint32(12, js.length + jp, true);
  dv.setUint32(16, CHUNK_JSON, true);
  u8.set(js, 20);
  u8.fill(0x20, 20 + js.length, 20 + js.length + jp);
  if (bin.length > 0) {
    const b = 20 + js.length + jp;
    dv.setUint32(b, bin.length + bp, true);
    dv.setUint32(b + 4, CHUNK_BIN, true);
    u8.set(bin, b + 8);
  }
  return out;
}

// ---- 4×4 column-major ----
function mul(a: M4, b: M4): M4 {
  const o = new Array<number>(16).fill(0);
  for (let c = 0; c < 4; c++) for (let r = 0; r < 4; r++) o[c * 4 + r] = a[r]! * b[c * 4]! + a[4 + r]! * b[c * 4 + 1]! + a[8 + r]! * b[c * 4 + 2]! + a[12 + r]! * b[c * 4 + 3]!;
  return o;
}
function invert(m: M4): M4 {
  // Affine inverse: [A t]⁻¹ = [A⁻¹ −A⁻¹t].
  const [a, b, c, d, e, f, g, h, i] = [m[0]!, m[4]!, m[8]!, m[1]!, m[5]!, m[9]!, m[2]!, m[6]!, m[10]!];
  const det = a * (e * i - f * h) - b * (d * i - f * g) + c * (d * h - e * g);
  if (Math.abs(det) < 1e-300) throw new FaceForwardRefusal('a node transform is singular');
  const k = 1 / det;
  const r = [
    (e * i - f * h) * k, (c * h - b * i) * k, (b * f - c * e) * k,
    (f * g - d * i) * k, (a * i - c * g) * k, (c * d - a * f) * k,
    (d * h - e * g) * k, (b * g - a * h) * k, (a * e - b * d) * k,
  ];
  const t = [m[12]!, m[13]!, m[14]!];
  return [
    r[0]!, r[3]!, r[6]!, 0,
    r[1]!, r[4]!, r[7]!, 0,
    r[2]!, r[5]!, r[8]!, 0,
    -(r[0]! * t[0]! + r[1]! * t[1]! + r[2]! * t[2]!), -(r[3]! * t[0]! + r[4]! * t[1]! + r[5]! * t[2]!), -(r[6]! * t[0]! + r[7]! * t[1]! + r[8]! * t[2]!), 1,
  ];
}
function local(n: Node): M4 {
  if (n.matrix !== undefined) return [...n.matrix];
  const [tx, ty, tz] = n.translation ?? [0, 0, 0];
  const [x, y, z, w] = n.rotation ?? [0, 0, 0, 1];
  const [sx, sy, sz] = n.scale ?? [1, 1, 1];
  const [x2, y2, z2] = [x! + x!, y! + y!, z! + z!];
  const [xx, xy, xz, yy, yz, zz, wx, wy, wz] = [x! * x2, x! * y2, x! * z2, y! * y2, y! * z2, z! * z2, w! * x2, w! * y2, w! * z2];
  return [
    (1 - (yy + zz)) * sx!, (xy + wz) * sx!, (xz - wy) * sx!, 0,
    (xy - wz) * sy!, (1 - (xx + zz)) * sy!, (yz + wx) * sy!, 0,
    (xz + wy) * sz!, (yz - wx) * sz!, (1 - (xx + yy)) * sz!, 0,
    tx!, ty!, tz!, 1,
  ];
}
const point = (m: M4, p: number[]): number[] => [
  m[0]! * p[0]! + m[4]! * p[1]! + m[8]! * p[2]! + m[12]!,
  m[1]! * p[0]! + m[5]! * p[1]! + m[9]! * p[2]! + m[13]!,
  m[2]! * p[0]! + m[6]! * p[1]! + m[10]! * p[2]! + m[14]!,
];
const vector = (m: M4, v: number[]): number[] => [
  m[0]! * v[0]! + m[4]! * v[1]! + m[8]! * v[2]!,
  m[1]! * v[0]! + m[5]! * v[1]! + m[9]! * v[2]!,
  m[2]! * v[0]! + m[6]! * v[1]! + m[10]! * v[2]!,
];

/** θ about +Y (column-major): x' = c·x + s·z, z' = −s·x + c·z. */
const rotY = (theta: number): M4 => {
  const c = Math.cos(theta);
  const s = Math.sin(theta);
  return [c, 0, -s, 0, 0, 1, 0, 0, s, 0, c, 0, 0, 0, 0, 1];
};
/** A body at least this far off +Z (degrees) is turned; nearer is a stance, not an error. */
export const FACE_FORWARD_MIN_DEG = 10;
/**
 * SIDEWAYS is left alone: a body 60°–120° off +Z is the native Tripo rig's own
 * convention (the goblin and kobold fixtures stand at +X), and Harry's family
 * frame priors assume it — turned, the goblin's hand graft refused ("Cannot
 * author hand frames"; the smoke, 2026-09-25). Backward and moderate yaws are
 * the render errors this exists for.
 */
const SIDEWAYS_DEG: readonly [number, number] = [60, 120];
const sideways = (yawDeg: number): boolean => {
  const a = Math.abs(angleDiff(yawDeg, 0));
  return a >= SIDEWAYS_DEG[0] && a <= SIDEWAYS_DEG[1];
};
/** Feet and shoulders must agree within this (degrees) before the body is turned. */
const FACE_FORWARD_AGREE_DEG = 20;
const SHOULDER = /(^|[:_ .])(l|left|r|right)[_ .]?(upper[_ .]?arm|arm)$|(^|[:_ .])upper[_ .]?arm[_ .]?(l|r)$/i;
const sideOf = (name: string): 'L' | 'R' | null =>
  /(^|[:_ .])(l|left)[_ .]?[a-z]|[_ .](l)$/i.test(name) && !/(^|[:_ .])(r|right)[_ .]?[a-z]/i.test(name) ? 'L' : /(^|[:_ .])(r|right)[_ .]?[a-z]|[_ .](r)$/i.test(name) ? 'R' : null;
/** Signed difference a − b, degrees, in (−180, 180]. */
const angleDiff = (a: number, b: number): number => {
  let d = (a - b) % 360;
  if (d <= -180) d += 360;
  if (d > 180) d -= 360;
  return d;
};

const FOOT = /(^|[:_ .])(l|left|r|right)[_ .]?foot$|(^|[:_ .])foot[_ .]?(l|r)$/i;

/** Measure the yaw; turn the body to face +Z when it is 10° or more off. Pure. */
export function faceForwardGlb(glb: ArrayBuffer): FaceForwardResult {
  const { json, bin } = parse(glb);
  const nodes = json.nodes ?? [];
  const skin = json.skins?.[0];
  if (skin === undefined) return { glb, turned: false, turnDeg: 0, facing: null, reason: 'no skin — facing not measured' };
  const parentOf = new Map<number, number>();
  nodes.forEach((n, i) => n.children?.forEach((c) => parentOf.set(c, i)));
  const worldOf = (i: number): M4 => {
    let m = local(nodes[i]!);
    for (let p = parentOf.get(i); p !== undefined; p = parentOf.get(p)) m = mul(local(nodes[p]!), m);
    return m;
  };
  const joints = new Set(skin.joints);
  const accessors = json.accessors ?? [];
  const views = json.bufferViews ?? [];
  const dv = new DataView(bin.buffer, bin.byteOffset, bin.byteLength);
  /** Element offsets of an accessor in the BIN chunk (FLOAT, not sparse), or a refusal. */
  const layout = (a: number, what: string): { at: (k: number, c: number) => number; comps: number; acc: Accessor } => {
    const acc = accessors[a]!;
    if (acc.sparse !== undefined) throw new FaceForwardRefusal(`${what} is sparse — facing cannot be turned exactly`);
    if (acc.componentType !== FLOAT || acc.bufferView === undefined) throw new FaceForwardRefusal(`${what} is not plain float data (quantized?) — facing cannot be turned exactly`);
    const bv = views[acc.bufferView]!;
    const comps = { SCALAR: 1, VEC2: 2, VEC3: 3, VEC4: 4, MAT4: 16 }[acc.type] ?? 0;
    const stride = bv.byteStride ?? comps * 4;
    const base = (bv.byteOffset ?? 0) + (acc.byteOffset ?? 0);
    return { at: (k, c) => base + k * stride + c * 4, comps, acc };
  };
  // Where the joint RENDERS: its world position through the node hierarchy. Not the
  // inverse bind matrix — that is the mesh's own space, and a node above the
  // skeleton can stand it round (the tripo goblin and kobold fixtures: ~90° in
  // mesh space, facing +Z as rendered; measured 2026-09-25).
  const bindWorld = (k: number): number[] => point(worldOf(skin.joints[k]!), [0, 0, 0]);
  void layout;
  void dv;

  // ---- upright? Only a Y-up body has a yaw to read. A Z-up file (the native
  //      Tripo fixtures lie along Z: box ±0.5 m in z) is stood up by the import's
  //      own up-axis handling; measured here it reads a nonsense "90° off". ----
  const headK = skin.joints.findIndex((j) => /(^|[:_ .])head$/i.test(nodes[j]!.name ?? ''));
  const footKs = skin.joints.map((j, k) => (FOOT.test(nodes[j]!.name ?? '') ? k : -1)).filter((k) => k >= 0);
  if (headK >= 0 && footKs.length > 0) {
    const h = bindWorld(headK);
    const f = footKs.map(bindWorld).reduce((a, p) => [a[0]! + p[0]! / footKs.length, a[1]! + p[1]! / footKs.length, a[2]! + p[2]! / footKs.length], [0, 0, 0]);
    const up = [h[0]! - f[0]!, h[1]! - f[1]!, h[2]! - f[2]!];
    if (up[1]! < 0.7 * Math.hypot(up[0]!, up[1]!, up[2]!)) {
      return { glb, turned: false, turnDeg: 0, facing: null, reason: 'not upright along +Y in the file (Z-up?) — facing left to the import' };
    }
  }

  // ---- measure: mean foot → toe over both feet, in the bind ----
  let fx = 0;
  let fz = 0;
  let feet = 0;
  skin.joints.forEach((j, k) => {
    if (!FOOT.test(nodes[j]!.name ?? '')) return;
    const toe = (nodes[j]!.children ?? []).find((c) => joints.has(c));
    if (toe === undefined) return;
    const a = bindWorld(k);
    const b = bindWorld(skin.joints.indexOf(toe));
    fx += b[0]! - a[0]!;
    fz += b[2]! - a[2]!;
    feet++;
  });
  if (feet === 0) return { glb, turned: false, turnDeg: 0, facing: null, reason: 'no foot → toe joints found — facing not measured, left as authored' };
  const facing = { x: fx / feet, z: fz / feet };
  const deg = (r: number): number => (r * 180) / Math.PI;
  // HOOVES: a toe joint on the ankle (under 1 cm ahead) gives no direction — the shoulders decide alone.
  const hooves = Math.hypot(facing.x, facing.z) < 0.01;
  const feetYaw = deg(Math.atan2(facing.x, facing.z));
  // The shoulder line confirms: forward = (left − right) × up.
  let shoulders: { L?: number[]; R?: number[] } = {};
  skin.joints.forEach((j, k) => {
    const name = nodes[j]!.name ?? '';
    if (!SHOULDER.test(name)) return;
    const side = sideOf(name);
    if (side !== null && shoulders[side] === undefined) shoulders = { ...shoulders, [side]: bindWorld(k) };
  });
  let shoulderYaw: number | null = null;
  if (shoulders.L !== undefined && shoulders.R !== undefined) {
    const lx = shoulders.L[0]! - shoulders.R[0]!;
    const lz = shoulders.L[2]! - shoulders.R[2]!;
    shoulderYaw = deg(Math.atan2(-lz, lx));
  }
  if (hooves) {
    if (shoulderYaw === null) return { glb, turned: false, turnDeg: 0, facing, reason: 'hooves (toes on the ankles) and no shoulders named — facing not measured, left as authored' };
    const said = `hooves; shoulders ${shoulderYaw.toFixed(1)}°`;
    if (Math.abs(angleDiff(shoulderYaw, 0)) < FACE_FORWARD_MIN_DEG) return { glb, turned: false, turnDeg: 0, facing, reason: `faces +Z (${said}) — as authored` };
    if (sideways(shoulderYaw)) return { glb, turned: false, turnDeg: 0, facing, reason: `stands sideways (${said}) — a rig convention, left as authored` };
    const turn = -angleDiff(shoulderYaw, 0);
    return { glb: turnGlbAboutY(glb, (turn * Math.PI) / 180), turned: true, turnDeg: turn, facing, reason: `faced ${angleDiff(shoulderYaw, 0).toFixed(1)}° off +Z (${said}) — turned ${turn.toFixed(1)}° to face +Z` };
  }
  if (shoulderYaw !== null && Math.abs(angleDiff(feetYaw, shoulderYaw)) > FACE_FORWARD_AGREE_DEG) {
    return {
      glb,
      turned: false,
      turnDeg: 0,
      facing,
      reason: `feet (${feetYaw.toFixed(1)}°) and shoulders (${shoulderYaw.toFixed(1)}°) disagree — facing left as authored`,
    };
  }
  const yaw = shoulderYaw === null ? feetYaw : feetYaw - angleDiff(feetYaw, shoulderYaw) / 2;
  const said = `feet ${feetYaw.toFixed(1)}°${shoulderYaw === null ? '' : `, shoulders ${shoulderYaw.toFixed(1)}°`}`;
  if (Math.abs(angleDiff(yaw, 0)) < FACE_FORWARD_MIN_DEG) {
    return { glb, turned: false, turnDeg: 0, facing, reason: `faces +Z (${said}) — as authored` };
  }
  if (sideways(yaw)) {
    return { glb, turned: false, turnDeg: 0, facing, reason: `stands sideways (${said}) — a rig convention (native Tripo), left as authored` };
  }
  const turn = -angleDiff(yaw, 0);
  return {
    glb: turnGlbAboutY(glb, (turn * Math.PI) / 180),
    turned: true,
    turnDeg: turn,
    facing,
    reason: `faced ${angleDiff(yaw, 0).toFixed(1)}° off +Z (${said}) — turned ${turn.toFixed(1)}° to face +Z`,
  };
}

/** The exact half turn about +Y (see the header) — no measurement. Pure. */
export function turnGlbHalfAboutY(glb: ArrayBuffer): ArrayBuffer {
  return turnGlbAboutY(glb, Math.PI);
}

/** The exact turn by `theta` radians about +Y of a skinned GLB (see the header) — no measurement. Pure. */
export function turnGlbAboutY(glb: ArrayBuffer, theta: number): ArrayBuffer {
  const TURN = rotY(theta);
  const UNTURN = rotY(-theta);
  const TURN_Q = [0, Math.sin(theta / 2), 0, Math.cos(theta / 2)];
  const { json, bin } = parse(glb);
  const nodes = json.nodes ?? [];
  const skin = json.skins?.[0];
  if (skin === undefined) throw new FaceForwardRefusal('no skin — nothing to turn');
  const parentOf = new Map<number, number>();
  nodes.forEach((n, i) => n.children?.forEach((c) => parentOf.set(c, i)));
  const worldOf = (i: number): M4 => {
    let m = local(nodes[i]!);
    for (let p = parentOf.get(i); p !== undefined; p = parentOf.get(p)) m = mul(local(nodes[p]!), m);
    return m;
  };
  const joints = new Set(skin.joints);
  const accessors = json.accessors ?? [];
  const views = json.bufferViews ?? [];
  const layout = (a: number, what: string): { at: (k: number, c: number) => number; comps: number; acc: Accessor } => {
    const acc = accessors[a]!;
    if (acc.sparse !== undefined) throw new FaceForwardRefusal(`${what} is sparse — facing cannot be turned exactly`);
    if (acc.componentType !== FLOAT || acc.bufferView === undefined) throw new FaceForwardRefusal(`${what} is not plain float data (quantized?) — facing cannot be turned exactly`);
    const bv = views[acc.bufferView]!;
    const comps = { SCALAR: 1, VEC2: 2, VEC3: 3, VEC4: 4, MAT4: 16 }[acc.type] ?? 0;
    const stride = bv.byteStride ?? comps * 4;
    const base = (bv.byteOffset ?? 0) + (acc.byteOffset ?? 0);
    return { at: (k, c) => base + k * stride + c * 4, comps, acc };
  };
  const out = new Uint8Array(bin);
  const odv = new DataView(out.buffer);
  const turnNode = (n: Node): void => {
    if (n.matrix !== undefined) {
      n.matrix = mul(TURN, n.matrix);
      return;
    }
    const t = n.translation ?? [0, 0, 0];
    n.translation = point(TURN, t);
    const [x, y, z, w] = n.rotation ?? [0, 0, 0, 1];
    // TURN_Q ⊗ q.
    const [ax, ay, az, aw] = TURN_Q as [number, number, number, number];
    n.rotation = [aw * x! + ax * w! + ay * z! - az * y!, aw * y! - ax * z! + ay * w! + az * x!, aw * z! + ax * y! - ay * x! + az * w!, aw * w! - ax * x! - ay * y! - az * z!];
  };
  // Top nodes: a joint or an unskinned mesh takes the turn; a skinned mesh node
  // keeps its transform (its vertices turn below); a plain wrapper passes it down.
  const sceneRoots = json.scenes?.[json.scene ?? 0]?.nodes ?? [];
  const walk = (i: number): void => {
    const n = nodes[i]!;
    if (joints.has(i) || (n.mesh !== undefined && n.skin === undefined)) {
      turnNode(n);
      return;
    }
    for (const c of n.children ?? []) walk(c);
  };
  for (const r of sceneRoots) walk(r);

  const done = new Set<number>();
  const rotateAccessor = (a: number, kind: 'point' | 'vector', m: M4, what: string, width: 3 | 4): void => {
    if (done.has(a)) return;
    done.add(a);
    const L = layout(a, what);
    const min = [Infinity, Infinity, Infinity];
    const max = [-Infinity, -Infinity, -Infinity];
    for (let k = 0; k < L.acc.count; k++) {
      const v = [0, 1, 2].map((c) => odv.getFloat32(L.at(k, c), true));
      const r = kind === 'point' ? point(m, v) : vector(m, v);
      for (let c = 0; c < 3; c++) {
        odv.setFloat32(L.at(k, c), r[c]!, true);
        const f = odv.getFloat32(L.at(k, c), true);
        min[c] = Math.min(min[c]!, f);
        max[c] = Math.max(max[c]!, f);
      }
    }
    void width;
    if (L.acc.min !== undefined && L.acc.max !== undefined && L.acc.min.length === 3) {
      L.acc.min = min;
      L.acc.max = max;
    }
  };
  // Skinned primitives: vertices turn in their own node's space (M⁻¹·R·M).
  nodes.forEach((n, i) => {
    if (n.skin === undefined || n.mesh === undefined) return;
    const M = worldOf(i);
    const K = mul(invert(M), mul(TURN, M));
    for (const p of json.meshes?.[n.mesh]?.primitives ?? []) {
      if (p.attributes['POSITION'] !== undefined) rotateAccessor(p.attributes['POSITION'], 'point', K, 'POSITION', 3);
      if (p.attributes['NORMAL'] !== undefined) rotateAccessor(p.attributes['NORMAL'], 'vector', K, 'NORMAL', 3);
      if (p.attributes['TANGENT'] !== undefined) rotateAccessor(p.attributes['TANGENT'], 'vector', K, 'TANGENT', 4);
      for (const t of p.targets ?? []) {
        for (const [key, a] of Object.entries(t)) if (key === 'POSITION' || key === 'NORMAL' || key === 'TANGENT') rotateAccessor(a, 'vector', K, `morph ${key}`, 3);
      }
    }
  });
  // Inverse bind matrices: IBM · R⁻¹.
  for (const s of json.skins ?? []) {
    if (s.inverseBindMatrices === undefined || done.has(s.inverseBindMatrices)) continue;
    done.add(s.inverseBindMatrices);
    const L = layout(s.inverseBindMatrices, 'the inverse bind matrices');
    for (let k = 0; k < L.acc.count; k++) {
      const ibm = Array.from({ length: 16 }, (_, c) => odv.getFloat32(L.at(k, c), true));
      const r = mul(ibm, UNTURN);
      for (let c = 0; c < 16; c++) odv.setFloat32(L.at(k, c), r[c]!, true);
    }
  }
  return build(json, out);
}
